import { Host, h, writeTask } from "@stencil/core";
import { mouseEventModifierKey } from "../../common/helpers";
import { removeDragImage } from "../../../common/utils";
const DISTANCE_TO_CHECKBOX_CUSTOM_VAR = "--ch-tree-view-item-distance-to-checkbox";
const INITIAL_LEVEL = 0;
// Selectors
const TREE_ITEM_TAG_NAME = "ch-tree-view-item";
const DIRECT_TREE_ITEM_CHILDREN = `:scope>${TREE_ITEM_TAG_NAME}`;
const FIRST_ENABLED_SUB_ITEM = `${TREE_ITEM_TAG_NAME}:not([disabled])`;
const LAST_SUB_ITEM = `:scope>${TREE_ITEM_TAG_NAME}:last-child`;
// Keys
const EXPANDABLE_ID = "expandable";
const ENTER_KEY = "Enter";
const ESCAPE_KEY = "Escape";
// Export Parts
const getCheckboxExportPart = (part) => `${part}:checkbox__${part}`;
const CHECKBOX_EXPORT_PARTS = [
    "container",
    "input",
    "option",
    "checked",
    "indeterminate"
]
    .map(getCheckboxExportPart)
    .join(",");
export class ChTreeViewItem {
    constructor() {
        this.caption = undefined;
        this.checkbox = false;
        this.checked = false;
        this.customRender = false;
        this.disabled = false;
        this.dragDisabled = false;
        this.dropDisabled = false;
        this.dragState = "none";
        this.downloading = false;
        this.editable = undefined;
        this.editing = false;
        this.expandableButton = "decorative";
        this.expandOnClick = true;
        this.expanded = false;
        this.lastItem = false;
        this.lazyLoad = false;
        this.leaf = false;
        this.leftImgSrc = undefined;
        this.level = INITIAL_LEVEL;
        this.indeterminate = false;
        this.metadata = undefined;
        this.rightImgSrc = undefined;
        this.selected = false;
        this.showDownloadingSpinner = true;
        this.showLines = "none";
        this.toggleCheckboxes = false;
    }
    #watcher;
    /**
     * Useful to ignore the checkbox change when it was committed from a children.
     */
    // eslint-disable-next-line @stencil-community/own-props-must-be-private
    #ignoreCheckboxChange = false;
    // Refs
    #headerRef;
    #inputRef;
    el;
    updateChildrenCheckedValue(newValue) {
        if (!this.toggleCheckboxes || this.leaf || this.#ignoreCheckboxChange) {
            this.#ignoreCheckboxChange = false;
            return;
        }
        const treeItems = this.#getDirectTreeItems();
        treeItems.forEach(treeItem => {
            if (treeItem.checked !== newValue || treeItem.indeterminate !== false) {
                treeItem.updateChecked(newValue, false);
            }
        });
    }
    handleEditingChange(isEditing) {
        if (!isEditing) {
            return;
        }
        document.body.addEventListener("click", this.#removeEditModeOnClick, {
            capture: true
        });
        // Wait until the input is rendered to focus it
        writeTask(() => {
            requestAnimationFrame(() => {
                if (this.#inputRef) {
                    this.#inputRef.focus();
                }
            });
        });
    }
    handleExpandedChange(isExpanded) {
        // Wait until all properties are updated before lazy loading. Otherwise, the
        // lazyLoad property could be updated just after the executing of the function
        setTimeout(() => {
            this.#lazyLoadItems(isExpanded);
        });
    }
    handleLasItemChange(isLastItem) {
        if (isLastItem && this.showLines) {
            // Use RAF to set the observer after the render method has completed
            requestAnimationFrame(() => {
                this.#setResizeObserver();
            });
        }
        else {
            this.#disconnectObserver();
        }
    }
    handleShowLinesChange(newShowLines) {
        if (newShowLines && this.lastItem) {
            this.#setResizeObserver();
        }
        else {
            this.#disconnectObserver();
        }
    }
    /**
     * Fired when the checkbox value of the control is changed.
     */
    checkboxChange;
    /**
     * Fired when the checkbox value of the control is changed. This event only
     * applies when the control has `toggleCheckboxes = true`
     */
    checkboxToggleChange;
    /**
     * Fired when the item is being dragged.
     */
    itemDragStart;
    /**
     * Fired when the item is no longer being dragged.
     */
    itemDragEnd;
    /**
     * Fired when the lazy control is expanded an its content must be loaded.
     */
    loadLazyContent;
    /**
     * Fired when the item is asking to modify its caption.
     */
    modifyCaption;
    /**
     * Fired when the user interacts with the control in a way that its reference
     * must be opened.
     */
    openReference;
    /**
     * Fired when the selected state is updated by user interaction on the
     * control.
     */
    selectedItemChange;
    updateCheckboxValue(event) {
        // No need to update the checkbox value based on the children checkbox
        if (!this.toggleCheckboxes || this.el === event.target) {
            return;
        }
        const updatedCheck = event.detail.checked;
        const treeItems = this.#getDirectTreeItems();
        // Check if all the items have the same value as the updated item
        const allItemsHaveTheSameCheckedValue = treeItems.every(treeItem => treeItem.checked === updatedCheck);
        const eventMustBeEmitted = this.checked !== updatedCheck ||
            this.indeterminate !== !allItemsHaveTheSameCheckedValue;
        this.#ignoreCheckboxChange = this.checked !== updatedCheck;
        this.checked = updatedCheck;
        this.indeterminate = !allItemsHaveTheSameCheckedValue;
        // Sync with the UI Model
        if (eventMustBeEmitted) {
            this.checkboxToggleChange.emit({
                id: this.el.id,
                checked: updatedCheck,
                indeterminate: !allItemsHaveTheSameCheckedValue
            });
        }
    }
    /**
     * Focus the next item in the tree. If the control is expanded, it focuses
     * the first subitem in its tree.
     */
    async focusNextItem(ctrlKeyPressed) {
        // Focus the first subitem if expanded
        if (!this.leaf && this.expanded) {
            const subItem = this.el.querySelector(FIRST_ENABLED_SUB_ITEM);
            // The tree item could be empty or downloading subitem, so it is uncertain
            // if the query won't fail
            if (subItem) {
                subItem.setFocus(ctrlKeyPressed);
                return;
            }
        }
        // Otherwise, focus the next sibling
        this.focusNextSibling(ctrlKeyPressed);
    }
    /**
     * Focus the next sibling item in the tree.
     */
    async focusNextSibling(ctrlKeyPressed) {
        const nextSiblingItem = this.el
            .nextElementSibling;
        // Focus the next sibling, if exists
        if (nextSiblingItem) {
            // If the next sibling is disabled, ask for its next sibling
            if (nextSiblingItem.disabled) {
                nextSiblingItem.focusNextItem(ctrlKeyPressed);
            }
            else {
                nextSiblingItem.setFocus(ctrlKeyPressed);
            }
            return;
        }
        // The item is the last one of the tree at the first level
        if (this.level === INITIAL_LEVEL) {
            return;
        }
        // Otherwise, ask the parent to focus the next sibling
        const parentItem = this.el.parentElement;
        parentItem.focusNextSibling(ctrlKeyPressed);
    }
    /**
     * Focus the previous item in the tree. If the previous item is expanded, it focuses
     * the last subitem in its tree.
     */
    async focusPreviousItem(ctrlKeyPressed) {
        const previousSiblingItem = this.el
            .previousElementSibling;
        // Focus last item of the previous sibling
        if (previousSiblingItem) {
            previousSiblingItem.focusLastItem(ctrlKeyPressed);
            return;
        }
        // The item is the first one of the tree at the first level
        if (this.level === INITIAL_LEVEL) {
            return;
        }
        // Otherwise, set focus in the parent element
        const parentItem = this.el.parentElement;
        // Check if the parent is not disabled
        if (parentItem.disabled) {
            parentItem.focusPreviousItem(ctrlKeyPressed);
            return;
        }
        parentItem.setFocus(ctrlKeyPressed);
    }
    /**
     * Focus the last item in its subtree. If the control is not expanded, it
     * focus the control.
     */
    async focusLastItem(ctrlKeyPressed) {
        // Focus the last subitem if expanded and not lazy loading
        if (!this.leaf && this.expanded) {
            const lastSubItem = this.el.querySelector(LAST_SUB_ITEM);
            // The tree item could be empty or downloading subitem, so it is uncertain
            // if the query won't fail
            if (lastSubItem) {
                lastSubItem.focusLastItem(ctrlKeyPressed);
                return;
            }
        }
        // If the last item is disabled, try to focus the previous sibling
        if (this.disabled) {
            this.focusPreviousItem(ctrlKeyPressed);
            return;
        }
        // Otherwise, it focuses the control
        this.setFocus(ctrlKeyPressed);
    }
    /**
     * Set focus in the control.
     */
    async setFocus(ctrlKeyPressed) {
        this.#headerRef.focus();
        // Normal navigation auto selects the item.
        if (!ctrlKeyPressed) {
            this.#setSelected();
        }
    }
    /**
     * Update `checked` and `indeterminate` properties.
     */
    async updateChecked(newChecked, newIndeterminate) {
        this.checked = newChecked;
        this.indeterminate = newIndeterminate;
        // Emit the event to sync with the UI model, even if the item does not
        // have toggleCheckboxes property
        this.checkboxToggleChange.emit({
            id: this.el.id,
            checked: newChecked,
            indeterminate: newIndeterminate
        });
    }
    #getDirectTreeItems = () => Array.from(this.el.querySelectorAll(DIRECT_TREE_ITEM_CHILDREN));
    #setResizeObserver = () => {
        this.#watcher = new ResizeObserver(() => {
            const distanceToCheckbox = this.el.getBoundingClientRect().height -
                this.#headerRef.getBoundingClientRect().height / 2;
            this.el.style.setProperty(DISTANCE_TO_CHECKBOX_CUSTOM_VAR, distanceToCheckbox + "px");
        });
        this.#watcher.observe(this.el);
        this.#watcher.observe(this.#headerRef);
    };
    #disconnectObserver = () => {
        if (!this.#watcher) {
            return;
        }
        this.#watcher.disconnect();
        this.#watcher = null;
    };
    #checkIfShouldRemoveEditMode = (event) => {
        event.stopPropagation();
        if (event.code !== ENTER_KEY && event.code !== ESCAPE_KEY) {
            return;
        }
        event.preventDefault();
        const commitEdition = event.code === ENTER_KEY;
        this.#removeEditMode(true, commitEdition)();
    };
    #removeEditModeOnClick = (event) => {
        // The click is executed outside the input and the pointer type is defined,
        // meaning that the button click was not triggered by the Enter or Space keys
        if (!event.composedPath().includes(this.#inputRef) && event.pointerType) {
            this.#removeEditMode(false)();
        }
    };
    #removeEditMode = (shouldFocusHeader, commitEdition = false) => () => {
        // When pressing the enter key in the input, the removeEditMode event is
        // triggered twice (due to the headerRef.focus() triggering the onBlur
        // event in the input), so we need to check if the edit mode was disabled
        if (!this.editing) {
            return;
        }
        this.editing = false;
        document.body.removeEventListener("click", this.#removeEditModeOnClick, {
            capture: true
        });
        const newCaption = this.#inputRef.value;
        if (commitEdition && newCaption.trim() !== "") {
            this.modifyCaption.emit({
                id: this.el.id,
                caption: newCaption
            });
        }
        if (shouldFocusHeader) {
            this.#headerRef.focus();
        }
    };
    #toggleExpand = (event) => {
        event.stopPropagation();
        if (!this.leaf) {
            this.expanded = !this.expanded;
        }
        this.selected = true;
        this.selectedItemChange.emit(this.#getSelectedInfo(mouseEventModifierKey(event), true));
    };
    #lazyLoadItems = (expanded) => {
        if (!this.lazyLoad || !expanded) {
            return;
        }
        // Load items
        this.lazyLoad = false;
        this.downloading = true;
        this.loadLazyContent.emit(this.el.id);
    };
    #toggleSelected = () => {
        const selected = !this.selected;
        this.selected = selected;
        this.selectedItemChange.emit(this.#getSelectedInfo(true, selected));
    };
    #setSelected = () => {
        this.selected = true;
        this.selectedItemChange.emit(this.#getSelectedInfo(false, true));
    };
    #toggleOrSelect = (event) => {
        // Ctrl key
        if (mouseEventModifierKey(event)) {
            this.#toggleSelected();
        }
        // Expand on click interaction
        else if (this.expandOnClick) {
            this.#toggleExpand(event);
        }
        // Click only selects the item
        else {
            this.#setSelected();
        }
    };
    #getSelectedInfo = (ctrlKeyPressed, selected) => ({
        ctrlKeyPressed: ctrlKeyPressed,
        expanded: this.expanded,
        id: this.el.id,
        metadata: this.metadata,
        parentId: this.el.parentElement?.id,
        selected: selected
    });
    #handleActionDblClick = (event) => {
        event.stopPropagation();
        if (mouseEventModifierKey(event)) {
            this.#toggleSelected();
            return;
        }
        this.#emitOpenReference();
        // The Control key is not pressed, so the control can be expanded if double
        // click expands the item
        if (!this.leaf && !this.expandOnClick) {
            this.#toggleExpand(event);
        }
    };
    /**
     * Event triggered by the following actions on the main button:
     *   - Click
     *   - Enter keydown
     *   - Space keydown and keyup
     */
    // eslint-disable-next-line @stencil-community/own-props-must-be-private
    #handleActionClick = (event) => {
        event.stopPropagation();
        // Don't perform actions when editing
        if (this.editing) {
            return;
        }
        event.preventDefault();
        // Click event
        if (event.pointerType) {
            this.#toggleOrSelect(event);
            return;
        }
        this.#emitOpenReference();
        // Enter or space
        this.#toggleExpand(event);
    };
    /**
     * Event triggered by key events on the main button.
     */
    // eslint-disable-next-line @stencil-community/own-props-must-be-private
    #handleActionKeyDown = (event) => {
        // Only toggle if the Enter key was pressed with the Ctrl key
        if (mouseEventModifierKey(event) && event.code === ENTER_KEY) {
            event.stopPropagation();
            this.#toggleSelected();
        }
    };
    #emitOpenReference = () => {
        this.openReference.emit({
            id: this.el.id,
            leaf: this.leaf,
            metadata: this.metadata
        });
    };
    #handleCheckedChange = (event) => {
        event.stopPropagation();
        const checked = event.target.checked;
        this.checked = checked;
        this.indeterminate = false; // Changing the checked value makes it no longer indeterminate
        this.checkboxChange.emit({
            id: this.el.id,
            checked: this.checked,
            indeterminate: false
        });
    };
    #renderImg = (cssClass, src) => (h("img", { "aria-hidden": "true", class: cssClass, part: cssClass, alt: "", src: src, loading: "lazy" }));
    #handleDragStart = (event) => {
        // Disallow drag when editing the caption
        if (this.editing) {
            event.preventDefault();
            event.stopPropagation();
            return;
        }
        removeDragImage(event);
        event.dataTransfer.effectAllowed = "move";
        this.dragState = "start";
        this.itemDragStart.emit({
            elem: this.el,
            dragEvent: event
        });
    };
    #handleDragEnd = () => {
        // event.preventDefault();
        // this.el.style.cursor = null;
        this.dragState = "none";
        this.itemDragEnd.emit();
    };
    #handleDrop = () => {
        this.dragState = "none";
    };
    componentWillLoad() {
        // Check if must lazy load
        this.#lazyLoadItems(this.expanded);
    }
    componentDidLoad() {
        if (this.lastItem && this.showLines) {
            this.#setResizeObserver();
        }
    }
    disconnectedCallback() {
        // If it was disconnected on edit mode, remove the body event handler
        if (this.editing) {
            this.#removeEditMode(false);
        }
        this.#disconnectObserver();
    }
    render() {
        const evenLevel = this.level % 2 === 0;
        const expandableButtonVisible = !this.leaf && this.expandableButton !== "no";
        const expandableButtonNotVisible = !this.leaf && this.expandableButton === "no";
        const acceptDrop = !this.dropDisabled && !this.leaf && this.dragState !== "start";
        const hasContent = !this.leaf && !this.lazyLoad;
        const showAllLines = this.showLines === "all" && this.level !== INITIAL_LEVEL;
        const showLastLine = this.showLines === "last" &&
            this.level !== INITIAL_LEVEL &&
            this.lastItem;
        return (h(Host, { role: "treeitem", "aria-level": this.level + 1, "aria-selected": this.selected ? "true" : null, class: {
                [TREE_ITEM_TAG_NAME + "--downloading"]: this.downloading,
                [TREE_ITEM_TAG_NAME + "--editing"]: this.editing,
                [TREE_ITEM_TAG_NAME + "--not-editing"]: !this.editing,
                [TREE_ITEM_TAG_NAME + "--drag-" + this.dragState]: this.dragState !== "none" && this.dragState !== "start",
                [TREE_ITEM_TAG_NAME + "--deny-drop"]: this.leaf
            }, style: { "--level": `${this.level}` },
            // Drag and drop
            onDrop: acceptDrop ? this.#handleDrop : null }, h("button", { "aria-controls": hasContent ? EXPANDABLE_ID : null, "aria-expanded": hasContent ? this.expanded.toString() : null, class: {
                header: true,
                "header--selected": this.selected,
                "header--disabled": this.disabled,
                "header--expandable-offset": expandableButtonVisible,
                "header--checkbox-offset": expandableButtonNotVisible && this.checkbox,
                "header--even": evenLevel,
                "header--odd": !evenLevel,
                "header--even-expandable": evenLevel && expandableButtonVisible,
                "header--odd-expandable": !evenLevel && expandableButtonVisible,
                "header--level-0": this.level === INITIAL_LEVEL,
                "expandable-button-decorative": !this.leaf && this.expandableButton === "decorative",
                "expandable-button-decorative--collapsed": !this.leaf &&
                    this.expandableButton === "decorative" &&
                    !this.expanded
            }, part: `header${this.disabled ? " disabled" : ""}${this.selected ? " selected" : ""}${this.level === INITIAL_LEVEL ? " level-0" : ""}`, type: "button", disabled: this.disabled, onClick: this.#handleActionClick, onKeyDown: !this.editing ? this.#handleActionKeyDown : null,
            // Drag and drop
            draggable: !this.dragDisabled, onDragStart: this.#handleDragStart, onDragEnd: !this.dragDisabled ? this.#handleDragEnd : null, ref: el => (this.#headerRef = el) }, !this.leaf && this.expandableButton === "action" && (h("button", { type: "button", class: {
                "expandable-button": true,
                "expandable-button--expanded": this.expanded,
                "expandable-button--collapsed": !this.expanded
            }, part: `expandable-button${this.disabled ? " disabled" : ""}${this.expanded ? " expanded" : " collapsed"}`, disabled: this.disabled, onClick: this.#toggleExpand })), this.checkbox && (h("ch-checkbox", { accessibleName: this.caption, class: "checkbox", exportparts: CHECKBOX_EXPORT_PARTS, part: `checkbox${this.disabled ? " disabled" : ""}${this.checked ? " checked" : ""}${this.indeterminate ? " indeterminate" : ""}`, checkedValue: "true", disabled: this.disabled, indeterminate: this.indeterminate, unCheckedValue: "false", value: `${this.checked}`, onInput: this.#handleCheckedChange })), this.customRender ? (h("slot", { name: "custom-content" })) : ([
            h("div", { class: {
                    action: true,
                    "readonly-mode": !this.editing
                }, part: `action${!this.editing ? " readonly-mode" : ""}${!this.leaf && this.expanded ? " expanded" : ""}`, onDblClick: !this.editing ? this.#handleActionDblClick : null }, this.leftImgSrc &&
                this.#renderImg("left-img", this.leftImgSrc), this.editable && this.editing ? (h("input", { class: "edit-name", part: "edit-name", disabled: this.disabled, type: "text", value: this.caption, onBlur: this.#removeEditMode(false), onKeyDown: this.#checkIfShouldRemoveEditMode, ref: el => (this.#inputRef = el) })) : (this.caption), this.rightImgSrc &&
                this.#renderImg("right-img", this.rightImgSrc)),
            this.showDownloadingSpinner && !this.leaf && this.downloading && (h("div", { class: "downloading", part: "downloading" }))
        ]), (showAllLines || showLastLine) && (h("div", { class: {
                "dashed-line": true,
                "last-all-line": showAllLines && this.lastItem,
                "last-line": showLastLine
            }, part: `dashed-line${this.lastItem ? " last-all-line" : ""}` }))), hasContent && (h("div", { role: "group", "aria-busy": (!!this.downloading).toString(), "aria-live": this.downloading ? "polite" : null, id: EXPANDABLE_ID, class: {
                expandable: true,
                "expandable--collapsed": !this.expanded,
                "expandable--lazy-loaded": !this.downloading
            }, part: `expandable${this.expanded ? " expanded" : " collapsed"}${!this.downloading ? " lazy-loaded" : ""}` }, h("slot", null)))));
    }
    static get is() { return "ch-tree-view-item"; }
    static get encapsulation() { return "shadow"; }
    static get originalStyleUrls() {
        return {
            "$": ["tree-view-item.scss"]
        };
    }
    static get styleUrls() {
        return {
            "$": ["tree-view-item.css"]
        };
    }
    static get properties() {
        return {
            "caption": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This attributes specifies the caption of the control"
                },
                "attribute": "caption",
                "reflect": false
            },
            "checkbox": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Set this attribute if you want display a checkbox in the control."
                },
                "attribute": "checkbox",
                "reflect": false,
                "defaultValue": "false"
            },
            "checked": {
                "type": "boolean",
                "mutable": true,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Set this attribute if you want the checkbox to be checked by default.\nOnly works if `checkbox = true`"
                },
                "attribute": "checked",
                "reflect": true,
                "defaultValue": "false"
            },
            "customRender": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Set this attribute if you want to set a custom render for the control, by\npassing a slot."
                },
                "attribute": "custom-render",
                "reflect": false,
                "defaultValue": "false"
            },
            "disabled": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This attribute lets you specify if the element is disabled.\nIf disabled, it will not fire any user interaction related event\n(for example, click event)."
                },
                "attribute": "disabled",
                "reflect": true,
                "defaultValue": "false"
            },
            "dragDisabled": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This attribute lets you specify if the drag operation is disabled in the\ncontrol. If `true`, the control can't be dragged."
                },
                "attribute": "drag-disabled",
                "reflect": false,
                "defaultValue": "false"
            },
            "dropDisabled": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This attribute lets you specify if the drop operation is disabled in the\ncontrol. If `true`, the control won't accept any drops."
                },
                "attribute": "drop-disabled",
                "reflect": false,
                "defaultValue": "false"
            },
            "dragState": {
                "type": "string",
                "mutable": true,
                "complexType": {
                    "original": "DragState",
                    "resolved": "\"enter\" | \"none\" | \"start\"",
                    "references": {
                        "DragState": {
                            "location": "local",
                            "path": "/home/runner/work/chameleon-controls-library/chameleon-controls-library/src/components/tree-view/tree-view-item/tree-view-item.tsx",
                            "id": "src/components/tree-view/tree-view-item/tree-view-item.tsx::DragState"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This property lets you define the current state of the item when it's\nbeing dragged."
                },
                "attribute": "drag-state",
                "reflect": false,
                "defaultValue": "\"none\""
            },
            "downloading": {
                "type": "boolean",
                "mutable": true,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This attribute lets you specify when items are being lazy loaded in the\ncontrol."
                },
                "attribute": "downloading",
                "reflect": false,
                "defaultValue": "false"
            },
            "editable": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This attribute lets you specify if the edit operation is enabled in the\ncontrol. If `true`, the control can edit its caption in place."
                },
                "attribute": "editable",
                "reflect": false
            },
            "editing": {
                "type": "boolean",
                "mutable": true,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Set this attribute when the item is in edit mode"
                },
                "attribute": "editing",
                "reflect": false,
                "defaultValue": "false"
            },
            "expandableButton": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "\"action\" | \"decorative\" | \"no\"",
                    "resolved": "\"action\" | \"decorative\" | \"no\"",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Specifies what kind of expandable button is displayed.\nOnly works if `leaf === false`.\n - `\"expandableButton\"`: Expandable button that allows to expand/collapse\n    the items of the control.\n - `\"decorative\"`: Only a decorative icon is rendered to display the state\n    of the item."
                },
                "attribute": "expandable-button",
                "reflect": false,
                "defaultValue": "\"decorative\""
            },
            "expandOnClick": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "`true` to expand the control on click interaction. If `false`, with mouse\ninteraction the control will only be expanded on double click."
                },
                "attribute": "expand-on-click",
                "reflect": false,
                "defaultValue": "true"
            },
            "expanded": {
                "type": "boolean",
                "mutable": true,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "If the item has a sub-tree, this attribute determines if the subtree is\ndisplayed."
                },
                "attribute": "expanded",
                "reflect": false,
                "defaultValue": "false"
            },
            "lastItem": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This attribute specifies if the control is the last items in its subtree"
                },
                "attribute": "last-item",
                "reflect": false,
                "defaultValue": "false"
            },
            "lazyLoad": {
                "type": "boolean",
                "mutable": true,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Determine if the items are lazy loaded when opening the first time the\ncontrol."
                },
                "attribute": "lazy-load",
                "reflect": false,
                "defaultValue": "false"
            },
            "leaf": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "The presence of this attribute determine whether the item contains a\nsubtree. `true` if the item does not have a subtree."
                },
                "attribute": "leaf",
                "reflect": false,
                "defaultValue": "false"
            },
            "leftImgSrc": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Set the left side icon from the available Gemini icon set : https://gx-gemini.netlify.app/?path=/story/icons-icons--controls"
                },
                "attribute": "left-img-src",
                "reflect": false
            },
            "level": {
                "type": "number",
                "mutable": false,
                "complexType": {
                    "original": "number",
                    "resolved": "number",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Level in the tree at which the item is placed."
                },
                "attribute": "level",
                "reflect": false,
                "defaultValue": "INITIAL_LEVEL"
            },
            "indeterminate": {
                "type": "boolean",
                "mutable": true,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "`true` if the checkbox's value is indeterminate."
                },
                "attribute": "indeterminate",
                "reflect": false,
                "defaultValue": "false"
            },
            "metadata": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This attribute represents additional info for the control that is included\nwhen dragging the item."
                },
                "attribute": "metadata",
                "reflect": false
            },
            "rightImgSrc": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Set the right side icon from the available Gemini icon set : https://gx-gemini.netlify.app/?path=/story/icons-icons--controls"
                },
                "attribute": "right-img-src",
                "reflect": false
            },
            "selected": {
                "type": "boolean",
                "mutable": true,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "This attribute lets you specify if the item is selected"
                },
                "attribute": "selected",
                "reflect": true,
                "defaultValue": "false"
            },
            "showDownloadingSpinner": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "`true` to show the downloading spinner when lazy loading the sub items of\nthe control."
                },
                "attribute": "show-downloading-spinner",
                "reflect": false,
                "defaultValue": "true"
            },
            "showLines": {
                "type": "string",
                "mutable": false,
                "complexType": {
                    "original": "TreeViewLines",
                    "resolved": "\"all\" | \"last\" | \"none\"",
                    "references": {
                        "TreeViewLines": {
                            "location": "import",
                            "path": "../tree-view/types",
                            "id": "src/components/tree-view/tree-view/types.ts::TreeViewLines"
                        }
                    }
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "`true` to display the relation between tree items and tree lists using\nlines."
                },
                "attribute": "show-lines",
                "reflect": false,
                "defaultValue": "\"none\""
            },
            "toggleCheckboxes": {
                "type": "boolean",
                "mutable": false,
                "complexType": {
                    "original": "boolean",
                    "resolved": "boolean",
                    "references": {}
                },
                "required": false,
                "optional": false,
                "docs": {
                    "tags": [],
                    "text": "Set this attribute if you want all the children item's checkboxes to be\nchecked when the parent item checkbox is checked, or to be unchecked when\nthe parent item checkbox is unchecked."
                },
                "attribute": "toggle-checkboxes",
                "reflect": false,
                "defaultValue": "false"
            }
        };
    }
    static get events() {
        return [{
                "method": "checkboxChange",
                "name": "checkboxChange",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Fired when the checkbox value of the control is changed."
                },
                "complexType": {
                    "original": "TreeViewItemCheckedInfo",
                    "resolved": "{ id: string; checked: boolean; indeterminate: boolean; }",
                    "references": {
                        "TreeViewItemCheckedInfo": {
                            "location": "import",
                            "path": "../tree-view/types",
                            "id": "src/components/tree-view/tree-view/types.ts::TreeViewItemCheckedInfo"
                        }
                    }
                }
            }, {
                "method": "checkboxToggleChange",
                "name": "checkboxToggleChange",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Fired when the checkbox value of the control is changed. This event only\napplies when the control has `toggleCheckboxes = true`"
                },
                "complexType": {
                    "original": "TreeViewItemCheckedInfo",
                    "resolved": "{ id: string; checked: boolean; indeterminate: boolean; }",
                    "references": {
                        "TreeViewItemCheckedInfo": {
                            "location": "import",
                            "path": "../tree-view/types",
                            "id": "src/components/tree-view/tree-view/types.ts::TreeViewItemCheckedInfo"
                        }
                    }
                }
            }, {
                "method": "itemDragStart",
                "name": "itemDragStart",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Fired when the item is being dragged."
                },
                "complexType": {
                    "original": "TreeViewItemDragStartInfo",
                    "resolved": "{ elem: HTMLChTreeViewItemElement; dragEvent: DragEvent; }",
                    "references": {
                        "TreeViewItemDragStartInfo": {
                            "location": "import",
                            "path": "../tree-view/types",
                            "id": "src/components/tree-view/tree-view/types.ts::TreeViewItemDragStartInfo"
                        }
                    }
                }
            }, {
                "method": "itemDragEnd",
                "name": "itemDragEnd",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Fired when the item is no longer being dragged."
                },
                "complexType": {
                    "original": "any",
                    "resolved": "any",
                    "references": {}
                }
            }, {
                "method": "loadLazyContent",
                "name": "loadLazyContent",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Fired when the lazy control is expanded an its content must be loaded."
                },
                "complexType": {
                    "original": "string",
                    "resolved": "string",
                    "references": {}
                }
            }, {
                "method": "modifyCaption",
                "name": "modifyCaption",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Fired when the item is asking to modify its caption."
                },
                "complexType": {
                    "original": "TreeViewItemNewCaption",
                    "resolved": "{ id: string; caption: string; }",
                    "references": {
                        "TreeViewItemNewCaption": {
                            "location": "import",
                            "path": "../tree-view/types",
                            "id": "src/components/tree-view/tree-view/types.ts::TreeViewItemNewCaption"
                        }
                    }
                }
            }, {
                "method": "openReference",
                "name": "openReference",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Fired when the user interacts with the control in a way that its reference\nmust be opened."
                },
                "complexType": {
                    "original": "TreeViewItemOpenReferenceInfo",
                    "resolved": "{ id: string; leaf: boolean; metadata: string; }",
                    "references": {
                        "TreeViewItemOpenReferenceInfo": {
                            "location": "import",
                            "path": "../tree-view/types",
                            "id": "src/components/tree-view/tree-view/types.ts::TreeViewItemOpenReferenceInfo"
                        }
                    }
                }
            }, {
                "method": "selectedItemChange",
                "name": "selectedItemChange",
                "bubbles": true,
                "cancelable": true,
                "composed": true,
                "docs": {
                    "tags": [],
                    "text": "Fired when the selected state is updated by user interaction on the\ncontrol."
                },
                "complexType": {
                    "original": "TreeViewItemSelected",
                    "resolved": "{ ctrlKeyPressed: boolean; expanded: boolean; id: string; metadata: string; parentId: string; selected: boolean; }",
                    "references": {
                        "TreeViewItemSelected": {
                            "location": "import",
                            "path": "../tree-view/types",
                            "id": "src/components/tree-view/tree-view/types.ts::TreeViewItemSelected"
                        }
                    }
                }
            }];
    }
    static get methods() {
        return {
            "focusNextItem": {
                "complexType": {
                    "signature": "(ctrlKeyPressed: boolean) => Promise<void>",
                    "parameters": [{
                            "name": "ctrlKeyPressed",
                            "type": "boolean",
                            "docs": ""
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        },
                        "HTMLChTreeViewItemElement": {
                            "location": "global",
                            "id": "global::HTMLChTreeViewItemElement"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "Focus the next item in the tree. If the control is expanded, it focuses\nthe first subitem in its tree.",
                    "tags": []
                }
            },
            "focusNextSibling": {
                "complexType": {
                    "signature": "(ctrlKeyPressed: boolean) => Promise<void>",
                    "parameters": [{
                            "name": "ctrlKeyPressed",
                            "type": "boolean",
                            "docs": ""
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        },
                        "HTMLChTreeViewItemElement": {
                            "location": "global",
                            "id": "global::HTMLChTreeViewItemElement"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "Focus the next sibling item in the tree.",
                    "tags": []
                }
            },
            "focusPreviousItem": {
                "complexType": {
                    "signature": "(ctrlKeyPressed: boolean) => Promise<void>",
                    "parameters": [{
                            "name": "ctrlKeyPressed",
                            "type": "boolean",
                            "docs": ""
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        },
                        "HTMLChTreeViewItemElement": {
                            "location": "global",
                            "id": "global::HTMLChTreeViewItemElement"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "Focus the previous item in the tree. If the previous item is expanded, it focuses\nthe last subitem in its tree.",
                    "tags": []
                }
            },
            "focusLastItem": {
                "complexType": {
                    "signature": "(ctrlKeyPressed: boolean) => Promise<void>",
                    "parameters": [{
                            "name": "ctrlKeyPressed",
                            "type": "boolean",
                            "docs": ""
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        },
                        "HTMLChTreeViewItemElement": {
                            "location": "global",
                            "id": "global::HTMLChTreeViewItemElement"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "Focus the last item in its subtree. If the control is not expanded, it\nfocus the control.",
                    "tags": []
                }
            },
            "setFocus": {
                "complexType": {
                    "signature": "(ctrlKeyPressed: boolean) => Promise<void>",
                    "parameters": [{
                            "name": "ctrlKeyPressed",
                            "type": "boolean",
                            "docs": ""
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "Set focus in the control.",
                    "tags": []
                }
            },
            "updateChecked": {
                "complexType": {
                    "signature": "(newChecked: boolean, newIndeterminate: boolean) => Promise<void>",
                    "parameters": [{
                            "name": "newChecked",
                            "type": "boolean",
                            "docs": ""
                        }, {
                            "name": "newIndeterminate",
                            "type": "boolean",
                            "docs": ""
                        }],
                    "references": {
                        "Promise": {
                            "location": "global",
                            "id": "global::Promise"
                        }
                    },
                    "return": "Promise<void>"
                },
                "docs": {
                    "text": "Update `checked` and `indeterminate` properties.",
                    "tags": []
                }
            }
        };
    }
    static get elementRef() { return "el"; }
    static get watchers() {
        return [{
                "propName": "checked",
                "methodName": "updateChildrenCheckedValue"
            }, {
                "propName": "editing",
                "methodName": "handleEditingChange"
            }, {
                "propName": "expanded",
                "methodName": "handleExpandedChange"
            }, {
                "propName": "lastItem",
                "methodName": "handleLasItemChange"
            }, {
                "propName": "showLines",
                "methodName": "handleShowLinesChange"
            }];
    }
    static get listeners() {
        return [{
                "name": "checkboxChange",
                "method": "updateCheckboxValue",
                "target": undefined,
                "capture": false,
                "passive": false
            }];
    }
}
//# sourceMappingURL=tree-view-item.js.map
